import { useQuery } from '@tanstack/react-query';
import { useUserLogged } from './useUserLogged';
import {
  fetchBitbucketProjects,
  BitbucketProject,
} from '@/apis/bitbucketProjects';

/**
 * Fetch the list of Bitbucket projects in the org's workspace. Only enabled
 * when the provider is Bitbucket; returns an empty list otherwise.
 */
export const useBitbucketProjects = () => {
  const { user, organization, status } = useUserLogged();

  const providerRaw =
    (organization as { Source?: string })?.Source ?? user?.provider ?? 'github';
  const provider =
    typeof providerRaw === 'string' ? providerRaw.toLowerCase() : 'github';
  const isBitbucket = provider === 'bitbucket';
  const orgUUID = organization?.OrgUUID ?? '';

  const query = useQuery({
    queryKey: ['bitbucket-projects', orgUUID, user?.UserUUID],
    queryFn: async (): Promise<BitbucketProject[]> => {
      if (!orgUUID) return [];
      const res = await fetchBitbucketProjects(orgUUID, user?.UserUUID ?? '');
      if (res.error) {
        console.error('Error fetching Bitbucket projects:', res.error);
      }
      return res.projects || [];
    },
    enabled: Boolean(orgUUID && status === 'authenticated' && isBitbucket),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    projects: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
